/**
 * DI-3-F (N2 "after verification") — how the runner learns that the pilot's
 * Approve answered `approved_pending_upload`: the verify card's `approveOne`
 * announces it here. DI-3-S1-FE — from intake/v1 1.17.0 the status and the
 * list row carry the approval (`approvedAt`), which decides the chip and the
 * task row in every tab; this record only bridges the seconds until the next
 * read (`verify/approval.ts:approvalOf`) and still starts this runner's upload.
 *
 *   * in THIS tab: every listener (the runner) is told at once; a runner that
 *     holds the set records `upload.approvedAt` (persisted through its own
 *     write chain) and starts the upload;
 *   * in the OTHER tabs of this browser: the same message on a
 *     BroadcastChannel — the tab that runs the runner may be another one;
 *   * no runner took it (no tab holds the session right now): the approval is
 *     written into the active session's record, so the runner that restores
 *     it later starts the upload.
 */
import {
  activeSessionId,
  getSession,
  updateSession,
} from "../session/sessionStore.js";

export const APPROVALS_CHANNEL = "aeriosiq-intake-approvals";

interface ApprovedMessage {
  type: "approved";
  daId: string;
  at: string;
}

/** A listener answers true when it holds the set (and records the approval). */
export type ApprovalListener = (daId: string, at: string) => boolean;

const listeners = new Set<ApprovalListener>();
let channel: BroadcastChannel | null | undefined;

function getChannel(): BroadcastChannel | null {
  if (channel !== undefined) return channel;
  channel = null;
  if (typeof BroadcastChannel === "undefined") return channel;
  try {
    const ch = new BroadcastChannel(APPROVALS_CHANNEL);
    // Node's BroadcastChannel (a test runner) keeps the process alive otherwise
    (ch as unknown as { unref?: () => void }).unref?.();
    ch.onmessage = (e: MessageEvent<Partial<ApprovedMessage> | null>) => {
      const m = e.data;
      if (m?.type === "approved" && m.daId && m.at) tell(m.daId, m.at);
    };
    channel = ch;
  } catch {
    channel = null;
  }
  return channel;
}

function tell(daId: string, at: string): boolean {
  let held = false;
  for (const l of listeners) if (l(daId, at)) held = true;
  return held;
}

export function onApproved(l: ApprovalListener): () => void {
  listeners.add(l);
  getChannel();
  return () => {
    listeners.delete(l);
  };
}

/** Write the approval into the active session's record (no runner took it). */
async function recordApproval(daId: string, at: string): Promise<void> {
  const sid = await activeSessionId();
  if (!sid) return;
  const rec = await getSession(sid);
  if (!rec?.clusters?.some((c) => c.dataAcquisitionId === daId)) return;
  await updateSession(sid, (r) => ({
    ...r,
    clusters: (r.clusters ?? []).map((c) =>
      c.dataAcquisitionId === daId && c.upload && !c.upload.approvedAt
        ? { ...c, upload: { ...c.upload, approvedAt: at } }
        : c,
    ),
  }));
}

/** Approve answered `approved_pending_upload` for `daId`. */
export function announceApproved(daId: string, now: Date = new Date()): void {
  const at = now.toISOString();
  const held = tell(daId, at);
  try {
    getChannel()?.postMessage({ type: "approved", daId, at });
  } catch {
    /* a closed channel: the record below still carries it */
  }
  if (!held) void recordApproval(daId, at);
}

/** Tests: forget every listener and the channel. */
export function resetApprovals(): void {
  listeners.clear();
  channel?.close();
  channel = undefined;
}
